'use client';

import { useRef, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

import { useChatContext } from '@/contexts/ChatContext';
import { UserMessage, AgentMessage } from './MessageItem';
import { motion, AnimatePresence } from 'framer-motion';
import ChatInput from './ChatInput';
import {
  ListChecks,
  User,
  MagnifyingGlass,
  PlusCircle,
} from 'phosphor-react';

const EXAMPLE_ACTIONS = [
  { icon: <ListChecks className="h-4 w-4" />, text: 'Listar todas as tasks do ClickUp' },
  { icon: <User className="h-4 w-4" />, text: 'Traga um resumo do perfil do LinkedIn' },
  { icon: <MagnifyingGlass className="h-4 w-4" />, text: 'Pesquise vagas de Tecnologia em São Paulo' },
  { icon: <PlusCircle className="h-4 w-4" />, text: 'Crie uma nova task no ClickUp' },
];

export default function Messages() {
  const { messages, getCurrentAgent, isStreaming, startTypingAnimation, stopTypingAnimation } = useChatContext();
  useTheme();
  const agent = getCurrentAgent();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming]);

  if (messages.length === 0) {
    return (
      <div className="h-full min-h-0 overflow-y-auto overflow-x-hidden">
        <EmptyState agentName={agent?.name} />
      </div>
    );
  }

  return (
    <div
      className="relative h-full min-h-0 overflow-y-auto overflow-x-hidden"
    >
      {/* Soft edge fades to make scroll feel smoother and keep focus on the content */}
      <div className="pointer-events-none sticky top-0 z-10 h-10 bg-gradient-to-b from-[color:var(--background)] to-transparent" />

      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-10 space-y-5">
        <AnimatePresence>
          {messages.map((message, index) => {
            const key = `${message.id}-${index}`;
            if (message.role === 'user') {
              return <UserMessage key={key} message={message} />;
            }
            return (
              <AgentMessage
                key={key}
                message={message}
                agentName={agent?.name}
                startTypingAnimation={startTypingAnimation}
                stopTypingAnimation={stopTypingAnimation}
              />
            );
          })}
        </AnimatePresence>
        <div ref={messagesEndRef} className="h-4" />
      </div>

      <div className="pointer-events-none sticky bottom-0 z-10 h-14 bg-gradient-to-t from-[color:var(--background)] to-transparent" />
    </div>
  );
}

interface EmptyStateProps {
  agentName?: string;
}

function EmptyState({ agentName }: EmptyStateProps) {
  const { setSuggestedMessage } = useChatContext();
  return (
    <div className="h-full flex flex-col items-center justify-center px-6 py-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-4xl mx-auto flex flex-col"
      >
        {/* Header Section */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center mb-4">
            {/* Animated logo with liquid metal effect */}
            <motion.div
              className="relative flex items-center justify-center"
              animate={{
                rotate: [0, 360],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'linear',
              }}
            >
              {/* Outer glow circle */}
              <motion.div
                className="absolute h-16 w-16 rounded-full"
                style={{
                  background: 'radial-gradient(circle, var(--nt-primary) 0%, transparent 70%)',
                  filter: 'blur(8px)',
                  opacity: 0.3,
                }}
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.2, 0.4, 0.2],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />
              
              {/* Logo SVG */}
              <div className="relative z-20 flex items-center justify-center">
                <motion.div
                  animate={{
                    scale: [1, 1.05, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  style={{ opacity: 0.9 }}
                >
                  <svg
                    fill="none"
                    height="48"
                    viewBox="0 0 40 48"
                    width="40"
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12"
                  >
                    <path
                      clipRule="evenodd"
                      d="m20 41.1364c1.8693 0 3.6688-.2994 5.353-.8527-1.7175-.2891-3.5271-.8718-5.3517-1.7289-1.8251.8573-3.6351 1.4402-5.3529 1.7294 1.6838.553 3.4828.8522 5.3516.8522zm-16.28276-11.7805c.28897-1.7183.87187-3.529 1.72942-5.3547-.8578-1.8258-1.44092-3.6366-1.73007-5.3551-.55352 1.6844-.85295 3.4842-.85295 5.3539 0 1.8704.29966 3.6709.8536 5.3559zm3.60425-2.0098c-.16001.4683-.29664.9297-.40971 1.382-.88774 3.5509-.23652 5.9879 1.06875 7.2932 1.30527 1.3052 3.74227 1.9565 7.29317 1.0687.4525-.1131.914-.2498 1.3824-.4098-1.7678-1.1549-3.5052-2.5591-5.1409-4.1948-1.63517-1.6352-3.03901-3.372-4.19371-5.1393zm12.67971 7.8448c-2.1428-1.1725-4.3241-2.7862-6.3647-4.8268-2.0401-2.0401-3.6535-4.2207-4.82586-6.363 1.17244-2.1428 2.78616-4.324 4.82676-6.3646 2.0404-2.0404 4.2213-3.6539 6.3638-4.8263 2.1424 1.1723 4.3231 2.7858 6.3632 4.826 2.0407 2.0407 3.6545 4.222 4.827 6.3649-1.1724 2.1424-2.7859 4.3231-4.8261 6.3633-2.0404 2.0405-4.2215 3.6541-6.3641 4.8265zm12.6793-7.8447c-1.1548 1.7674-2.5586 3.5043-4.1939 5.1395-1.6354 1.6355-3.3726 3.0395-5.1402 4.1944.4682.1599.9295.2965 1.3817.4096 3.5509.8877 5.9879.2365 7.2932-1.0688 1.3052-1.3052 1.9565-3.7422 1.0687-7.2932-.113-.4521-.2496-.9133-.4095-1.3815zm1.8748-3.345c.8572-1.8245 1.44-3.634 1.7293-5.3514.5528 1.6834.8518 3.4819.8518 5.3502 0 1.869-.2993 3.6682-.8524 5.3521-.2891-1.7172-.8718-3.5265-1.7287-5.3509zm-9.2009-16.28444c-1.7179.28914-3.528.87204-5.3531 1.72941-1.8256-.85764-3.6361-1.44069-5.3544-1.72984 1.6842-.55336 3.4837-.85269 5.3531-.85269 1.8699 0 3.6698.29949 5.3544.85312zm8.7037 2.05754c-3.6125-3.57015-8.5779-5.7743-14.0581-5.7743-11.0457 0-20 8.9543-20 20s8.9543 20 20 20c5.488 0 10.4598-2.2104 14.0735-5.7896.0232-.0225.0462-.0453.0691-.0681.023-.023.0458-.0461.0684-.0693 3.5788-3.6137 5.789-8.5853 5.789-14.073 0-5.4788-2.203-10.443-5.7715-14.0552-.028-.02903-.0564-.05785-.085-.08647-.0283-.02828-.0568-.05629-.0854-.08403zm-17.4017 1.5465c-.4688-.1602-.9307-.2971-1.3835-.4103-3.551-.8877-5.98798-.2365-7.29325 1.0688s-1.9565 3.7423-1.06876 7.2932c.11322.4529.25005.9148.41032 1.3838 1.15492-1.7679 2.55915-3.5054 4.19489-5.1411 1.6354-1.6355 3.3727-3.0396 5.1403-4.1944zm16.0244 9.3353c-1.155-1.7679-2.5593-3.5055-4.1951-5.1413-1.6352-1.6352-3.3722-3.0391-5.1396-4.1939.4686-.1601.9303-.2969 1.3829-.41 3.5509-.8877 5.9879-.2365 7.2932 1.0687 1.3052 1.3053 1.9565 3.7423 1.0687 7.2933-.1132.4527-.2499.9145-.4101 1.3832z"
                      fill="var(--nt-primary)"
                      fillRule="evenodd"
                    />
                  </svg>
                </motion.div>
              </div>

              {/* Liquid metal effect layers */}
              <motion.div
                className="absolute h-24 w-24 rounded-full"
                style={{
                  background: 'radial-gradient(circle, var(--nt-primary) 0%, transparent 60%)',
                  filter: 'blur(14px)',
                  opacity: 0.15,
                }}
                animate={{
                  rotate: [0, 360],
                  scale: [0.5, 0.6, 0.5],
                }}
                transition={{
                  rotate: {
                    duration: 10,
                    repeat: Infinity,
                    ease: 'linear',
                  },
                  scale: {
                    duration: 3,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  },
                }}
              />
              <motion.div
                className="absolute h-24 w-24 rounded-full"
                style={{
                  background: 'radial-gradient(circle, var(--nt-primary) 0%, transparent 60%)',
                  filter: 'blur(8px)',
                  opacity: 0.1,
                }}
                animate={{
                  rotate: [360, 0],
                  scale: [0.55, 0.65, 0.55],
                }}
                transition={{
                  rotate: {
                    duration: 12,
                    repeat: Infinity,
                    ease: 'linear',
                  },
                  scale: {
                    duration: 4,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  },
                }}
              />
            </motion.div>
          </div>
          <h1 className="mb-4 text-5xl font-bold theme-text">
            O que você quer criar?
          </h1>
          <p className="text-lg theme-text-muted">
            Descreva sua tarefa e o {agentName || 'Paladino'} irá lidar com o resto.{' '}
            <span className="theme-text font-medium">Desenvolva</span> e{' '}
            <span className="theme-text font-medium">automatize</span> seus projetos.
          </p>
        </div>

        {/* ChatInput Section - Centered and Open */}
        <div className="mx-auto mb-6 w-full max-w-2xl">
          <ChatInput />
        </div>

        {/* Quick Actions */}
        <div className="mx-auto mt-8 flex w-full max-w-3xl flex-wrap justify-center gap-2">
          {EXAMPLE_ACTIONS.map((action, index) => (
            <motion.button
              key={index}
              type="button"
              className="inline-flex items-center gap-2 rounded-full border theme-border bg-[color:var(--card-bg)]/50 px-4 py-2 text-xs theme-text hover:bg-[color:var(--card-bg)] transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSuggestedMessage(action.text)}
            >
              {action.icon}
              <span>{action.text}</span>
            </motion.button>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

